import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Logo from '../components/common/Logo';
import { COUNTIES, GENDER_OPTIONS } from '../utils/constants';
import { validateMpesaNumber, normalizeMpesaNumber } from '../utils/validators';
import { saveProfileStep1, getUserProfile } from '../services/firestore';
import useAuthStore from '../stores/authStore';

const ProfileStep1 = () => {
  const [formData, setFormData] = useState({
    county: '',
    mpesaNumber: '',
    nationalId: '',
    gender: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    // Load existing profile data if available
    const loadProfile = async () => {
      if (user?.uid) {
        try {
          const result = await getUserProfile(user.uid);
          if (result.success && result.profile) {
            setFormData(prev => ({
              ...prev,
              county: result.profile.county || '',
              mpesaNumber: result.profile.mpesaNumber || '',
              nationalId: result.profile.nationalId || '',
              gender: result.profile.gender || ''
            }));
          }
        } catch (error) {
          console.error('Load profile error:', error);
        }
      }
    };

    loadProfile();
  }, [user, isAuthenticated, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.county) {
      newErrors.county = 'County is required';
    }

    if (!formData.mpesaNumber.trim()) {
      newErrors.mpesaNumber = 'M-Pesa number is required';
    } else {
      // Remove spaces for validation
      const cleanNumber = formData.mpesaNumber.replace(/\s/g, '');
      if (!validateMpesaNumber(cleanNumber)) {
        newErrors.mpesaNumber = 'Please enter a valid M-Pesa number (07XXXXXXXX or 01XXXXXXXX)';
      }
    }

    if (!formData.nationalId.trim()) {
      newErrors.nationalId = 'National ID is required';
    } else if (!/^\d{8}$/.test(formData.nationalId)) {
      newErrors.nationalId = 'National ID must be 8 digits';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    
    try {
      // Keep M-Pesa number in local format - no normalization
      const dataToSave = {
        ...formData,
        mpesaNumber: formData.mpesaNumber.replace(/\s/g, '') // Just remove spaces
      };
      
      const result = await saveProfileStep1(user.uid, dataToSave);
      
      if (result.success) {
        navigate('/profile-step-2');
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      console.error('Save error:', error);
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/auth');
  };

  return (
    <div className="page-container">
      <div className="form-container">
        <div className="text-center mb-6">
          <Logo size="lg" className="mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Personal Information
          </h1>
          <p className="text-gray-600 mb-4">
            Step 1 of 2 - Basic Details
          </p>
          
          {/* Step indicator */}
          <div className="step-indicator">
            <div className="step-dot step-dot-active"></div>
            <div className="step-dot step-dot-inactive"></div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Select
            label="County"
            name="county"
            options={COUNTIES}
            value={formData.county}
            onChange={handleInputChange}
            error={errors.county}
            placeholder="Select your county"
            required
          />

          <Input
            label="M-Pesa Number"
            type="tel"
            name="mpesaNumber"
            placeholder="07X XXX XXXX"
            value={formData.mpesaNumber}
            onChange={handleInputChange}
            error={errors.mpesaNumber}
            required
          />

          <Input
            label="National ID Number"
            type="tel"
            name="nationalId"
            placeholder="Enter 8-digit National ID"
            value={formData.nationalId}
            onChange={handleInputChange}
            error={errors.nationalId}
            maxLength={8}
            required
          />

          <Select
            label="Gender"
            name="gender"
            options={GENDER_OPTIONS}
            value={formData.gender}
            onChange={handleInputChange}
            error={errors.gender}
            placeholder="Select your gender"
            required
          />

          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-red-700 text-sm">{errors.general}</span>
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="flex-1"
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              type="submit"
              size="lg"
              className="flex-1"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                'Next'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileStep1;